import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DtAnnotationCanvas from "./DtAnnotationCanvas";
import DtProgressBar from "./DtProgressBar";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faBan } from "@fortawesome/pro-regular-svg-icons";
import { updateSelectedSeenAnnotationsList, updateSkippedAnnotationsList, updateTrainingProgress } from "redux/actions/driverTrainingActions";
import { asyncLoadImage, getUrlDataForFrame, keySourceLookup } from "components/util/PlaylistUtils";
import { MEMOIZED_DOMAIN_URL } from "components/util/HostUtils";
import _ from "lodash";

const selectedSessionProgressSelector = (state) => _.get(state.driverTraining.trainee.selectedSession, ["progress", "completed"], []);
const selectedSessionSeenSelector = (state) => _.get(state.driverTraining.trainee.selectedSession, ["progress", "seen"], []);
const selectedSessionSkippedSelector = (state) => _.get(state.driverTraining.trainee.selectedSession, ["progress", "skipped"], []);
const playlistVideoDataSelector = (state) => state.playlist.data.video;
const selectedSessionSelector = (state) => state.driverTraining.trainee.selectedSession;
const csrfTokenSelector = (state) => state.csrfToken;

const DtImageWithBbox = ({ annotation, first, distance, skippedAnnotationsIDs, adjustedTriggerDistance, currentSpeed }) => {
    const dispatch = useDispatch();

    const imgRef = useRef();
    const previousDistanceRef = useRef(distance);
    const selectedSessionProgress = useSelector(selectedSessionProgressSelector);
    const selectedSessionSeenData = useSelector(selectedSessionSeenSelector);
    const selectedSessionSkipped = useSelector(selectedSessionSkippedSelector);
    const playlistVideoData = useSelector(playlistVideoDataSelector);
    const selectedSession = useSelector(selectedSessionSelector);
    const csrfToken = useSelector(csrfTokenSelector);

    const [gettingCloser, setGettingCloser] = useState(true);
    const [annotationImageWidth, setAnnotationImageWidth] = useState(434);
    const [annotationImageHeight, setAnnotationImageHeight] = useState(249);
    const [progressUpdated, setProgressUpdated] = useState(false);
    const [imageData, setImageData] = useState(null);

    const deviceKey = useMemo(() => _.get(selectedSession, "device_key"), [selectedSession]);
    const sessionKey = useMemo(() => _.get(selectedSession, "session_key"), [selectedSession]);

    // this will check if the distance to the object is increassing or decreasing
    useEffect(() => {
        if (distance > previousDistanceRef.current && currentSpeed > 2) {
            setGettingCloser(false);
        } else if (distance < previousDistanceRef.current) {
            setGettingCloser(true);
        }
        previousDistanceRef.current = distance;
    }, [annotation.id, currentSpeed, distance]);

    const checkAnnotationImgDimensions = () => {
        if (imgRef && imgRef.current) {
            setAnnotationImageHeight(imgRef.current.offsetHeight);
            setAnnotationImageWidth(imgRef.current.offsetWidth);
        }
    };

    // when session went pass annotation, display for another 2secs and hide
    const hideAnnotation = useCallback(
        (newSeenList) => {
            dispatch(updateSelectedSeenAnnotationsList(newSeenList));
        },
        [dispatch],
    );

    const isCompleted = useMemo(() => _.includes(selectedSessionProgress, String(annotation.id)), [annotation.id, selectedSessionProgress]);
    const wasSkipped = useMemo(() => _.includes(selectedSessionSkipped, annotation.id), [annotation.id, selectedSessionSkipped]);

    useEffect(() => {
        if (!progressUpdated && !isCompleted && !gettingCloser) {
            if (!skippedAnnotationsIDs.includes(annotation.id)) {
                // if annotation was skipped before
                if (wasSkipped) {
                    let newSkippedList = _.remove(selectedSessionSkipped, (id) => id === annotation.id);
                    dispatch(updateSkippedAnnotationsList(newSkippedList));
                }
                let newCompletedList = [...selectedSessionProgress, String(annotation.id)];
                dispatch(updateTrainingProgress(newCompletedList));
            } else {
                let newSkippedList = [...selectedSessionSkipped, annotation.id];
                dispatch(updateSkippedAnnotationsList(newSkippedList));
            }
            setProgressUpdated(true);
        } else if (!gettingCloser) {
            if (!_.includes(selectedSessionSeenData, annotation.id)) {
                let newSeenList = [...selectedSessionSeenData, annotation.id];
                hideAnnotation(newSeenList);
            }
        }
    }, [
        annotation.id,
        dispatch,
        distance,
        gettingCloser,
        hideAnnotation,
        isCompleted,
        progressUpdated,
        selectedSessionProgress,
        selectedSessionSeenData,
        selectedSessionSkipped,
        skippedAnnotationsIDs,
        wasSkipped,
    ]);

    // Function to be triggered when the image is loaded to draw bbox in right place
    useEffect(() => {
        const handleImageLoad = () => {
            checkAnnotationImgDimensions();
        };

        if (imgRef && imgRef.current && typeof imgRef.current.addEventListener === "function") {
            imgRef.current.addEventListener("load", handleImageLoad);
        }

        return () => {
            if (imgRef && imgRef.current && typeof imgRef.current.removeEventListener === "function") {
                imgRef.current.removeEventListener("load", handleImageLoad);
            }
        };
    }, []);

    const keySource = useMemo(() => keySourceLookup(annotation.videoKey, playlistVideoData), [annotation.videoKey, playlistVideoData]);

    useEffect(() => {
        if (!keySource) {
            return;
        }

        let sourceIndex = keySource[1];
        let playlistIndex = keySource[0];
        let frame = annotation.frame;

        const playlistVideoDataForIndex = _.get(playlistVideoData, [sourceIndex], []);

        const urlData = getUrlDataForFrame(playlistVideoDataForIndex, playlistIndex, frame);
        const url = `https://raw${MEMOIZED_DOMAIN_URL}/${deviceKey}/${sessionKey}/${urlData.imageFile}?csrf=${csrfToken}`;
        const range = urlData.range;

        asyncLoadImage(url, range, (imageData) => {
            if (imageData) {
                setImageData(imageData);
            }
        });

        return () => {
            setImageData(null);
        };
    }, [csrfToken, annotation, deviceKey, sessionKey, keySource, playlistVideoData]);

    return (
        <div className={`UpcomingAnnotationItem ${first ? "Current" : "NextUp"}`}>
            <img
                src={imageData}
                alt="Next annotation"
                ref={imgRef}
                crossOrigin={"anonymous"}
            />
            <div className="AnnotationLabel">
                <DtAnnotationCanvas
                    key={annotation.id}
                    annotation={annotation}
                    width_={annotationImageWidth}
                    height_={annotationImageHeight}
                    small={!first}
                    bBoxVisible={true}
                />
            </div>
            {isCompleted ? (
                <div className="AnnotationCompletedOverlay">
                    <FontAwesomeIcon
                        color="green"
                        icon={faCheck}
                        size={first ? "3x" : "2x"}
                    />
                </div>
            ) : (
                wasSkipped && (
                    <div className="AnnotationSkippedOverlay">
                        <FontAwesomeIcon
                            color="red"
                            icon={faBan}
                            size={first ? "3x" : "2x"}
                        />
                    </div>
                )
            )}
            <div className="AnnotationProgress">{gettingCloser && <DtProgressBar progress={100 - (distance / adjustedTriggerDistance) * 100} />}</div>
        </div>
    );
};

export default DtImageWithBbox;
